import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
} from 'firebase/auth';
import { auth } from '../firebase';
import {
  addAuditLog,
  fetchAuditLogs,
  fetchAdminByUid,
} from '../services/firestoreService';

const AdminAuthContext = createContext(null);

export const AdminAuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        const adminDoc = await fetchAdminByUid(firebaseUser.uid);
        if (adminDoc && adminDoc.status !== 'inactive') {
          setAdmin({
            id: adminDoc.uid,
            uid: adminDoc.uid,
            name: adminDoc.name,
            email: adminDoc.email,
            role: adminDoc.role,
            status: adminDoc.status || 'active',
          });
        } else {
          await signOut(auth);
          setAdmin(null);
        }
      } else {
        setAdmin(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const logActivity = useCallback(
    async (action, details = '') => {
      if (!admin) return;

      try {
        await addAuditLog({
          adminId: admin.email,
          adminName: admin.name,
          adminEmail: admin.email,
          adminRole: admin.role,
          action,
          details: typeof details === 'object' ? JSON.stringify(details) : details,
          ipAddress: '127.0.0.1',
        });
      } catch (err) {
        console.error('Failed to log activity:', err);
      }
    },
    [admin]
  );

  const adminLogin = async (email, password) => {
    try {
      const credential = await signInWithEmailAndPassword(auth, email, password);
      const adminDoc = await fetchAdminByUid(credential.user.uid);

      if (!adminDoc || adminDoc.status === 'inactive') {
        await signOut(auth);
        return { success: false, message: 'You do not have admin access.' };
      }

      const adminToStore = {
        id: adminDoc.uid,
        uid: adminDoc.uid,
        name: adminDoc.name,
        email: adminDoc.email,
        role: adminDoc.role,
        status: adminDoc.status || 'active',
      };

      setAdmin(adminToStore);

      setTimeout(() => {
        logActivity('ADMIN_LOGIN', { email: adminDoc.email, role: adminDoc.role });
      }, 100);

      return { success: true, message: 'Login successful!' };
    } catch (error) {
      const messages = {
        'auth/invalid-credential': 'Invalid email or password!',
        'auth/user-not-found': 'Invalid email or password!',
        'auth/wrong-password': 'Invalid email or password!',
        'auth/too-many-requests': 'Too many attempts. Please try again later.',
      };
      return {
        success: false,
        message: messages[error.code] || 'Login failed. Please try again.',
      };
    }
  };

  const adminLogout = async () => {
    if (admin) {
      await logActivity('ADMIN_LOGOUT', { email: admin.email, role: admin.role });
    }
    await signOut(auth);
    setAdmin(null);
  };

  const getAuditLogs = async (filters = {}) => {
    try {
      return await fetchAuditLogs(filters);
    } catch {
      return [];
    }
  };

  const hasPermission = (requiredRole) => {
    if (!admin) return false;
    if (admin.role === 'superadmin') return true;
    return admin.role === requiredRole;
  };

  const isSuperAdmin = () => admin && admin.role === 'superadmin';

  const value = {
    admin,
    loading,
    adminLogin,
    adminLogout,
    logActivity,
    getAuditLogs,
    hasPermission,
    isSuperAdmin,
    isAuthenticated: !!admin,
  };

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
};

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
};
